import { LinkElementType } from '@treetop/shared/model';

export class HtmlUtil {
  /**
   * Removes links from an HTML string that match the specified target unique ID and element type.

   * This method parses the HTML string, identifies anchor (`<a>`) elements with specific `data-element-id` and `data-element-type` attributes,
   * and removes those elements from the HTML. The modified HTML string is then returned.

   * @param {string} html - The HTML string from which to remove links.
   * @param {string} targetUniqueId - The unique ID used to identify which links to removed. If not provided, all links with the specified element types will be removed.
   * @returns {string} - The modified HTML string with the specified links removed.
   */
  public static removeLinksFromHtml(html: string, targetUniqueId?: string): string {
    const doc = new DOMParser().parseFromString(html ?? '', 'text/html');

    doc.querySelectorAll('a').forEach((a) => {
      const elementId = a.getAttribute('data-element-id');
      const elementType = a.getAttribute('data-element-type');

      const isTargetType = elementType === String(LinkElementType.Widget) || elementType === String(LinkElementType.FlowchartNode);

      const matchesId = targetUniqueId ? elementId === targetUniqueId : true;

      if (isTargetType && matchesId) {
        a.remove();
      }
    });

    return doc.body.innerHTML;
  }

  /**
   * Removes specific line breaks from an HTML string.

   * Note: The second replacement is a workaround and may change based on updates
   * to the Froala WYSIWYG editor (see: https://froala.com/wysiwyg-editor/examples/enter/).
   *
   * @param {string} htmlString - The HTML string to modify.
   * @returns {string} - The modified HTML string with specific line breaks removed.
   */
  public static sanitizeHTMLForSVG = (htmlString: string): string => {
    return (
      htmlString
        // SVG/XML does not understand HTML entities like &nbsp;
        // convert to numeric entity which is XML-safe
        .replace(/&nbsp;/g, '&#160;')

        // normalize plain HTML <br> to XML self-closing form
        .replace(/<br>/g, '<br/>')

        // normalize any <br ...> (with attributes) to self-closing <br/>
        // required because SVG foreignObject must be valid XML
        .replace(/<br\b[^>]*>/g, '<br/>')

        // remove ASCII control chars that are illegal in XML and can break parsing
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')

        // remove invalid <0x..> artifacts sometimes produced by copy/paste or encoders
        .replace(/<0x(?:0[0-8bB-fF]|1[0-9a-fA-F]|7[fF])>/g, '')

        // remove non-characters forbidden by XML spec
        .replace(/[\uFFFE\uFFFF]/g, '')

        // remove lone UTF-16 surrogate halves which create invalid XML
        .replace(/[\uD800-\uDFFF]/g, '')

        // convert HTML void <col> to XML-safe self-closing <col/>
        // avoid matching already self-closed tags (<col/>)
        .replace(/<col\b([^/>]*?)>/g, '<col$1/>')

        // remove zero-width/invisible characters that can break layout or parsing
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
    );
  };
}
